import React from 'react';

import {Checkbox, Paper, Stack, Typography} from '@mui/material';
import {Square as SquareIcon} from '@mui/icons-material';
import {defineMessages, useIntl} from 'react-intl';

import {Palette} from './Palette';
import useMonitorMap from './useMonitorMap';

const msgs = defineMessages({
  textFootprint: {
    id: 'map.MapLegend.text.footprint',
    defaultMessage: 'Footprint',
  },
  textRadar: {
    id: 'map.MapLegend.text.radar',
    defaultMessage: 'Radar',
  },
  textFlightPath: {
    id: 'map.MapLegend.text.flightPath',
    defaultMessage: 'Flight path',
  },
  textGeoFence: {
    id: 'map.MapLegend.text.geoFence',
    defaultMessage: 'Geo fence',
  },
  textSafePort: {
    id: 'map.MapLegend.text.safePort',
    defaultMessage: 'Safe port',
  },
  textNoFlyZone: {
    id: 'map.MapLegend.text.noFlyZone',
    defaultMessage: 'No-fly zone',
  },
});

interface RowProps {
  color: string;
  label: string;
  fill?: boolean;
  enabled?: boolean;
  onToggle?: () => void;
}

function Row({color, label, fill, enabled = true, onToggle}: RowProps): JSX.Element {
  return (
    <Stack
      gap={1 / 2}
      direction="row"
      onClick={onToggle}
      sx={{alignItems: 'center', cursor: onToggle ? 'pointer' : 'default', opacity: enabled ? 1 : 0.45}}>
      <Checkbox
        size="small"
        checked={enabled}
        readOnly
        tabIndex={-1}
        sx={{p: 0, pointerEvents: 'none'}}
        icon={
          <SquareIcon
            fontSize="small"
            style={{
              fillOpacity: 0.5,
              fill: fill ? color : undefined,
              stroke: color,
              color: 'transparent',
              strokeWidth: 3,
            }}
          />
        }
        checkedIcon={
          <SquareIcon
            fontSize="small"
            style={{
              fillOpacity: 0.5,
              fill: fill ? color : undefined,
              stroke: color,
              color: 'transparent',
              strokeWidth: 3,
            }}
          />
        }
      />
      <Typography variant="caption" sx={{lineHeight: 0}}>
        {label}
      </Typography>
    </Stack>
  );
}

interface Props {
  enableRadar: boolean;
  interactive?: boolean;
}

function MapLegend({enableRadar, interactive = false}: Props): JSX.Element {
  const intl = useIntl();
  const {
    showFootprint,
    setShowFootprint,
    showRadar,
    setShowRadar,
    showFlightPath,
    setShowFlightPath,
    showGeoFence,
    setShowGeoFence,
    showSafePort,
    setShowSafePort,
    showNoFlyZone,
    setShowNoFlyZone,
  } = useMonitorMap();
  return (
    <Paper variant="outlined" sx={{p: 3 / 4}}>
      <Stack gap={1 / 4}>
        {enableRadar ? (
          <>
            <Row
              color={Palette.Path}
              label={intl.formatMessage(msgs.textFootprint)}
              enabled={interactive ? showFootprint : true}
              onToggle={interactive ? () => setShowFootprint?.(!showFootprint) : undefined}
            />
            <Row
              color={Palette.Radar}
              label={intl.formatMessage(msgs.textRadar)}
              enabled={interactive ? showRadar : true}
              onToggle={interactive ? () => setShowRadar?.(!showRadar) : undefined}
            />
          </>
        ) : null}
        <Row
          color={Palette.FlightPath}
          label={intl.formatMessage(msgs.textFlightPath)}
          fill
          enabled={interactive ? showFlightPath : true}
          onToggle={interactive ? () => setShowFlightPath?.(!showFlightPath) : undefined}
        />
        <Row
          color={Palette.GeoFence}
          label={intl.formatMessage(msgs.textGeoFence)}
          fill
          enabled={interactive ? showGeoFence : true}
          onToggle={interactive ? () => setShowGeoFence?.(!showGeoFence) : undefined}
        />
        <Row
          color={Palette.SafePort}
          label={intl.formatMessage(msgs.textSafePort)}
          fill
          enabled={interactive ? showSafePort : true}
          onToggle={interactive ? () => setShowSafePort?.(!showSafePort) : undefined}
        />
        <Row
          color={Palette.NoFlyZone}
          label={intl.formatMessage(msgs.textNoFlyZone)}
          fill
          enabled={interactive ? showNoFlyZone : true}
          onToggle={interactive ? () => setShowNoFlyZone?.(!showNoFlyZone) : undefined}
        />
      </Stack>
    </Paper>
  );
}

export default MapLegend;
